import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ChatCircle, DownloadSimple } from '@phosphor-icons/react';
import { ReviewPanel } from '../../../../../../components/client/review-panel';
import { ReviewStage } from '../../../../../../components/client/review-stage';
import { Icon } from '../../../../../../components/Icon';
import { EmptyState } from '../../../../../../components/ui/empty-state';
import { IconButton } from '../../../../../../components/ui/icon-button';
import { useToast } from '../../../../../../components/ui/toast';
import { signedDownloadUrl } from '../../../../../../lib/asset-url';
import { sentAgo } from '../../../../../../lib/client-review-display';
import { TASK_TYPE_META } from '../../../../../../lib/task-display';
import { clientReviewQuery } from '../../../../../../lib/tasks-api';
import { buttonClassName } from '../../../../../../components/ui/button';
import { PageSkeleton } from '../../../../../../components/ui/skeleton';
import { LoadFailure } from '../../../../../../components/ui/state-views';
import { toasts } from '../../../../../../lib/toast-presets';

export const Route = createFileRoute('/subdomain/app/_client/work/review/$taskId')({
  component: ClientReviewPage,
});

const PAGE =
  'flex flex-col gap-(--mbm-space-6) px-(--mbm-space-4) py-(--mbm-space-6) lg:gap-(--mbm-space-7) lg:px-(--mbm-space-9) lg:pb-(--mbm-space-7) lg:pt-(--mbm-space-8)';

/** The client's review page (Figma C05.2): the work on a sky, the answer beside it, and the versions under it. */
function ClientReviewPage() {
  const { taskId } = Route.useParams();
  const toast = useToast();
  const { data: review, isLoading, error, refetch } = useQuery({ ...clientReviewQuery(taskId), retry: false });
  const [picked, setPicked] = useState<string>();
  const kind = (error as { kind?: string } | null)?.kind;

  if (isLoading) {
    return (
      <div className={PAGE}>
        <PageSkeleton label="Loading your review" />
      </div>
    );
  }

  if (!review || review.versions.length === 0) {
    const card = 'rounded-(--mbm-component-radius-widget) bg-surface-card shadow-(--mbm-shadow-table-card)';
    const back = (
      <Link to="/subdomain/app/projects" search={{ q: undefined }} className={buttonClassName({ size: 'state' })}>
        Back to projects
      </Link>
    );
    return (
      <div className={PAGE}>
        {/* A task the client can't see answers 404/403: to them that is simply "nothing here yet", not an error. */}
        {review || kind === 'not_found' || kind === 'forbidden' ? (
          <div className={card}>
            <EmptyState
              title="Nothing to review here yet"
              description="When your team sends this over, it appears here for you to approve."
              action={back}
            />
          </div>
        ) : (
          <LoadFailure error={error} noun="task" className={card} action={back} onRetry={() => void refetch()} />
        )}
      </div>
    );
  }

  const version = review.versions.find((v) => v.id === picked) ?? review.versions[0]!;
  const type = TASK_TYPE_META[review.task.taskType];
  const download = async () => {
    try {
      for (const asset of version.assets)
        window.open(await signedDownloadUrl(asset.key), '_blank', 'noopener,noreferrer');
    } catch {
      toast({ title: 'Couldn’t open the files', tone: 'error' });
    }
  };

  return (
    <div className={PAGE}>
      <header className="flex flex-wrap items-end justify-between gap-(--mbm-space-4)">
        <div className="flex flex-col gap-(--mbm-space-1)">
          <Link
            to="/subdomain/app/projects/$projectId"
            params={{ projectId: review.task.projectId }}
            className="mb-(--mbm-space-1) inline-flex items-center gap-(--mbm-space-2) self-start text-label-medium text-text-secondary hover:text-text-primary focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)"
          >
            <Icon icon={ArrowLeft} size="sm" />
            {review.task.projectName}
          </Link>
          <h1 className="text-heading-page text-text-strong">{review.task.title}</h1>
          <p className="text-body-compact text-text-secondary">
            {type.label} · version {version.versionNumber} · sent {sentAgo(version.sentAt)}
          </p>
        </div>
        <div className="flex items-center gap-(--mbm-space-3)">
          <IconButton
            icon={ChatCircle}
            aria-label="Message your team"
            onClick={() => toast(toasts.comingSoon('Chat arrives with the messaging step.'))}
          />
          <IconButton
            icon={DownloadSimple}
            aria-label="Download the files"
            disabled={version.assets.length === 0}
            onClick={() => void download()}
          />
        </div>
      </header>

      <div className="grid items-start gap-(--mbm-space-6) lg:grid-cols-[minmax(0,660fr)_minmax(0,397fr)]">
        <ReviewStage review={review} version={version} onSelect={setPicked} />
        <ReviewPanel key={version.id} review={review} version={version} />
      </div>
    </div>
  );
}
